<script>
import { ref, toRefs, watch, inject, computed } from '@vue/composition-api';
import { MapBoxMixin } from '@/mixins/MapBoxMixin';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { ASSET_GROUP_DEFAULT_ICON, ASSET_GROUP_MARKER_DEFAULT_COLOUR } from '@/utils/constants';

export default {
    name: 'AssetGroupMarker',
    mixins: [MapBoxMixin, GeneralMixin],
    props: {
        assetGroup: {
            type: Object,
            default: () => {}
        },
        markers: {
            type: Array,
            default: () => {}
        },
        isDateToday: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            markerGeojson: {
                type: 'FeatureCollection',
                features: []
            },
            defaultIcon: ASSET_GROUP_DEFAULT_ICON,
            defaultMarkerColour: ASSET_GROUP_MARKER_DEFAULT_COLOUR
        };
    },
    setup(props, { root }) {
        const map = inject('map');
        const bounds = inject('bounds');
        const { getters } = inject('vuex-store');

        const { isDateToday, assetGroup } = toRefs(props);

        const pinnedUser = computed(() => getters['map/pinnedUser']);
        const isPlaying = computed(() => getters['map/isPlaying']);

        const marker = ref(null);

        if (!assetGroup.value.routeDetails || assetGroup.value.routeDetails.coordinates === null) {
            return marker;
        }

        watch(assetGroup, (newVal) => {
            if (!isDateToday.value) {
                return true;
            }
            const {
                routeDetails: {
                    coordinates: { latitude, longitude }
                }
            } = newVal;

            const pos = [longitude, latitude];

            if (!pinnedUser.value || pinnedUser.value === newVal.trackerReference) {
                marker.value.setLngLat(pos);
            }
            return true;
        });

        return {
            marker,
            map,
            bounds,
            pinnedUser,
            isPlaying
        };
    },
    render: () => null,
    mounted() {
        this.showMarker();
    },
    watch: {
        isPlaying(newVal) {
            if (
                !newVal &&
                this.isDateToday &&
                (!this.pinnedUser || this.assetGroup.trackerReference === this.pinnedUser)
            ) {
                this.addMarker();
                this.fitBounds([[marker.getLngLat().lng, marker.getLngLat().lat]]);
            } else {
                this.removeMarker();
            }
        },
        isDateToday(newVal) {
            if (
                newVal &&
                (!this.pinnedUser || (this.pinnedUser && this.assetGroup.trackerReference === this.pinnedUser))
            ) {
                this.addMarker();

                this.fitBounds([[marker.getLngLat().lng, marker.getLngLat().lat]]);
            } else {
                this.removeMarker();
            }
        },
        async pinnedUser(newVal) {
            if (!newVal && this.marker && this.isDateToday) {
                this.addMarker();
                this.fitBounds(this.markers.map((x) => [x.getLngLat().lng, x.getLngLat().lat]));
            } else if (this.marker) {
                if (newVal === this.assetGroup.trackerReference && this.isDateToday) {
                    this.addMarker();
                    this.fitBounds([[this.marker.getLngLat().lng, this.marker.getLngLat().lat]]);
                } else {
                    this.removeMarker();
                }
            }
        }
    },
    methods: {
        showMarker() {
            if (!this.assetGroup?.routeDetails?.coordinates) {
                return;
            }
            const { latitude, longitude } = this.assetGroup.routeDetails.coordinates;

            const pins = {
                icon: this.assetGroup.icon ?? this.defaultIcon,
                color: this.assetGroup.iconColour ?? this.defaultMarkerColour
            };
            this.marker = this.$_mapBox_setMarkers(this.map, latitude, longitude, 'asset', pins);

            this.marker.trackerReference = this.assetGroup.trackerReference;
            this.marker.getElement().addEventListener('click', () => {
                this.$root.$emit('marker-clicked', { data: this.assetGroup, marker: this.marker, type: 'assetGroup' });
            });

            this.addMarker();
            this.fitBounds(this.markers.map((x) => [x.getLngLat().lng, x.getLngLat().lat]));

            const markerLngLat = this.marker.getLngLat();

            // update location.
            const onDrop = () => {
                dispatch('map/SET_DRIVER_LOCATION', {
                    publicUserId: null,
                    trackerId: this.assetGroup.trackerId,
                    location: {
                        latitude: markerLngLat.lat,
                        longitude: markerLngLat.long
                    }
                });
            };

            // eslint-disable-next-line no-underscore-dangle
            if (this.marker._draggable) {
                this.$_mapBox_handleListener(this.map, 'mouseover', () => {
                    // eslint-disable-next-line no-underscore-dangle
                    if (!this.marker?._element) {
                        return;
                    }
                    // eslint-disable-next-line no-underscore-dangle
                    const mapImageMarker = Array.from(this.marker._element.children).find(
                        (x) => x.className === 'asset-map-image-marker'
                    );
                    if (mapImageMarker) {
                        if (mapImageMarker.getAttribute('markerDropListener') !== 'true') {
                            mapImageMarker.setAttribute('markerDropListener', 'true');
                            mapImageMarker.addEventListener('click', () => {
                                onDrop();
                            });
                        }
                    }
                });
            }

            if (!this.isDateToday) {
                this.removeMarker();
            }

            if (this.pinnedUser && this.pinnedUser !== this.assetGroup.trackerReference) {
                this.removeMarker();
            }
        },
        removeMarker() {
            this.$emit('deleteMarker', this.marker);
        },
        fitBounds(bounds) {
            const coordinates = this.$_mapBox_getMaxBounds(bounds);
            this.$_mapBox_callMapFunction(this.map, 'fitBounds', coordinates, false);
        },
        addMarker() {
            if (!this.markers.some((x) => x.trackerReference === this.marker.trackerReference)) {
                this.markers.push(this.marker);
                this.$_mapBox_addMapElement(this.marker, this.map);
            }
        }
    }
};
</script>
